import { useMemo } from 'react';
import { DragDropContext, Draggable, Droppable, type DropResult } from '@hello-pangea/dnd';
import { Badge, Box, Group, Paper, Text, Tooltip } from '@mantine/core';

import type { Group as GroupType, Member } from '../api';
import { numStyle } from '../theme';

/** Droppable id sloupce pro lidi bez skupiny — číselné id tu nemáme. */
const NO_GROUP = 'none';

const COLUMN_WIDTH = 224;

interface Props {
  /** Už profiltrovaní členové — hledání a filtry řeší stránka nad tím. */
  members: Member[];
  groups: GroupType[];
  onEdit: (member: Member) => void;
  /** groupId === null znamená vyndat ze skupiny. */
  onMove: (member: Member, groupId: number | null) => void;
}

interface Column {
  /** Zároveň droppableId: číslo skupiny jako text, nebo NO_GROUP. */
  key: string;
  name: string;
  color: string | null;
  members: Member[];
}

/**
 * Tabule členů: sloupec na skupinu, přetažením karty se člen přesune.
 *
 * Sloupce vevnitř záměrně nescrollují — vertikálně scrolluje celá stránka.
 * Vnořený scroll se s autoscrollem při tažení pere a na dotyku z toho bývá
 * nepoužitelná kombinace. Vodorovně scrolluje jen řada sloupců.
 */
export function MembersBoard({ members, groups, onEdit, onMove }: Props) {
  const columns = useMemo<Column[]>(() => {
    const byGroup = new Map<string, Member[]>();
    for (const m of members) {
      const key = m.group_id == null ? NO_GROUP : String(m.group_id);
      const bucket = byGroup.get(key);
      if (bucket) bucket.push(m);
      else byGroup.set(key, [m]);
    }

    const sorted = (list: Member[] | undefined) =>
      [...(list ?? [])].sort((a, b) => {
        // Bez čísla až za očíslované, pak abecedně — stejně jako v seznamu.
        const an = a.number == null;
        const bn = b.number == null;
        if (an !== bn) return an ? 1 : -1;
        if (an) return a.name.localeCompare(b.name, 'cs');
        return (a.number as number) - (b.number as number);
      });

    // "Bez skupiny" je první schválně: po zkopírování členů z jiné epochy jsou
    // všichni tady a odsud se rozdělují do skupin.
    return [
      {
        key: NO_GROUP,
        name: 'Bez skupiny',
        color: null,
        members: sorted(byGroup.get(NO_GROUP)),
      },
      ...groups.map((g) => ({
        key: String(g.id),
        name: g.name,
        color: g.color,
        members: sorted(byGroup.get(String(g.id))),
      })),
    ];
  }, [members, groups]);

  const handleDragEnd = (result: DropResult) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId) return;

    const member = members.find((m) => String(m.id) === draggableId);
    if (!member) return;

    onMove(member, destination.droppableId === NO_GROUP ? null : Number(destination.droppableId));
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <Box style={{ overflowX: 'auto', paddingBottom: 8 }}>
        <Group align="stretch" gap="sm" wrap="nowrap" style={{ minWidth: 'min-content' }}>
          {columns.map((col) => (
            <BoardColumn key={col.key} column={col} onEdit={onEdit} />
          ))}
        </Group>
      </Box>
    </DragDropContext>
  );
}

function BoardColumn({ column, onEdit }: { column: Column; onEdit: (m: Member) => void }) {
  return (
    <Paper
      withBorder
      radius="md"
      style={{
        width: COLUMN_WIDTH,
        flex: `0 0 ${COLUMN_WIDTH}px`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        px="xs"
        py={6}
        style={{
          borderBottom: '1px solid var(--mantine-color-default-border)',
          borderTop: `3px solid ${column.color || 'var(--mantine-color-gray-5)'}`,
        }}
      >
        <Group gap={6} wrap="nowrap" justify="space-between">
          <Text fz="sm" fw={600} truncate>{column.name}</Text>
          <Badge size="sm" variant="light" color="gray" style={numStyle}>
            {column.members.length}
          </Badge>
        </Group>
      </Box>

      <Droppable droppableId={column.key}>
        {(provided, snapshot) => (
          <Box
            ref={provided.innerRef}
            {...provided.droppableProps}
            p={6}
            style={{
              flex: 1,
              minHeight: 96,
              background: snapshot.isDraggingOver
                ? 'var(--mantine-color-default-hover)'
                : undefined,
              transition: 'background 120ms',
            }}
          >
            {column.members.map((m, index) => (
              <MemberCard key={m.id} member={m} index={index} onEdit={onEdit} />
            ))}
            {provided.placeholder}
            {column.members.length === 0 && !snapshot.isDraggingOver && (
              <Text fz="xs" c="dimmed" ta="center" mt="sm">Přetáhni sem</Text>
            )}
          </Box>
        )}
      </Droppable>
    </Paper>
  );
}

function MemberCard({
  member,
  index,
  onEdit,
}: {
  member: Member;
  index: number;
  onEdit: (m: Member) => void;
}) {
  const noNumber = member.number == null;

  return (
    <Draggable draggableId={String(member.id)} index={index}>
      {(provided, snapshot) => (
        // Schválně div, ne UnstyledButton. Knihovna odmítne začít tažení, které
        // vzniklo na interaktivním prvku (isAnInteractiveElement, 'button' je v
        // jejím seznamu), takže s tlačítkem se karta vykreslila, ale nešla táhnout.
        // dragHandleProps dodají tabIndex, klávesnicí jde karta zaostřit; mezerník
        // si bere knihovna na zvednutí, Enter proto necháváme na editaci.
        <Box
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={() => onEdit(member)}
          onKeyDown={(event) => {
            const { key } = event;
            if (key === 'Enter') {
              event.preventDefault();
              onEdit(member);
            }
          }}
          aria-label={`Upravit ${member.name}`}
          style={{
            display: 'block',
            width: '100%',
            marginBottom: 6,
            padding: '6px 8px',
            borderRadius: 'var(--mantine-radius-sm)',
            border: '1px solid var(--mantine-color-default-border)',
            background: 'var(--mantine-color-body)',
            cursor: 'grab',
            boxShadow: snapshot.isDragging ? 'var(--mantine-shadow-md)' : undefined,
            ...provided.draggableProps.style,
          }}
        >
          <Group gap={6} wrap="nowrap">
            {noNumber ? (
              <Tooltip label="Bez čísla — mobilní aplikace člena nezobrazí">
                <Badge size="sm" variant="outline" color="gray" style={numStyle}>—</Badge>
              </Tooltip>
            ) : (
              <Badge size="sm" variant="light" color="gray" style={numStyle}>{member.number}</Badge>
            )}
            <Text fz="sm" truncate>{member.name}</Text>
          </Group>
        </Box>
      )}
    </Draggable>
  );
}

